import React, { useState, lazy, useEffect, useCallback, Suspense } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "tss-react/mui";
import NavBar from "./NavBar";
import TopBar from "./TopBar";
import AuthenticateAPIKey from "src/api/AuthenticateAPIKey";
import { Helmet } from "react-helmet";
import LoadingScreen from "src/components/LoadingScreen";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Container, useMediaQuery, useTheme } from "@mui/material";
import "@ux-engineering/metal/css/metal.css";
import { API_COIN } from "src/constants";

const coin = API_COIN;

const useStyles = makeStyles()(theme => ({
    root: {
        backgroundColor: theme.palette.background.dark,
        display: "flex",
        height: "100%",
        overflow: "hidden",
        width: "100%",
    },
    wrapper: {
        display: "flex",
        flex: "1 1 auto",
        overflow: "hidden",
        paddingTop: 64,
        [theme.breakpoints.up("md")]: {
            paddingLeft: 256,
        },
    },
    contentContainer: {
        display: "flex",
        flex: "1 1 auto",
        overflow: "hidden",
    },
    content: {
        flex: "1 1 auto",
        height: "100%",
        overflow: "auto",
        padding: "0",
    },
    metalBackground: {
        backgroundColor: "var(--neutral-1)",
    },
    metalWrapper: {
        backgroundColor: "var(--neutral-1)",
        display: "flex",
        flex: "1 1 auto",
        overflow: "hidden",
        [theme.breakpoints.up("md")]: {
            paddingLeft: 256,
        },
    },
}));

const ViewerDashboardLayout = ({ children }) => {
    const theme = useTheme();
    const mobileDevice = useMediaQuery(theme.breakpoints.down("md"));
    const location = useLocation();
    function getInitialWorkerStatus(urlParams, subAccount, apiKey) {
        if (urlParams.get("selectedWorkerStatus")) {
            //removes the unnecessary parameters in the URL
            window.history.replaceState(
                null,
                null,
                window.location.pathname +
                    "?subaccount=" +
                    subAccount +
                    "&coin=" +
                    coin +
                    "&api_key=" +
                    apiKey
            );

            return urlParams.get("selectedWorkerStatus");
        }

        return null;
    }

    const { classes, cx } = useStyles();
    const [isMobileNavOpen, setMobileNavOpen] = useState(false);
    const urlParams = new URLSearchParams(window.location.search);
    const subAccount = urlParams.get("subaccount");
    const [setCoin] = useState(urlParams.get("coin"));
    const apiKey = urlParams.get("api_key");
    const workerStatus = getInitialWorkerStatus(urlParams, subAccount, apiKey);

    const [authenticated, setAuthenticated] = useState(null);

    // components for viewer dashboards
    var AppRedirectionComp = () => (
        <Navigate to={{ pathname: "dashboard", search: urlParams.toString() }} />
    );
    var DashboardViewerComp = lazy(() => import("src/views/reports/HashrateView"));
    var WorkersViewerComp = lazy(() => import("src/views/viewerDashboards/WorkersDashboard"));
    var DailyHashrateViewerComp = lazy(() =>
        import("src/views/viewerDashboards/DailyHashrateDashboard")
    );

    const fetchData = useCallback(async () => {
        const urlParams = new URLSearchParams(window.location.search);
        const apiKey = urlParams.get("api_key");
        const response = await AuthenticateAPIKey(apiKey);
        if (response.success) {
            setAuthenticated(true);
        } else {
            setAuthenticated(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleLocationName = () => {
        switch (location.pathname.split("/")[2]) {
            case "workers":
                return "Workers";

            case "daily-hashrate":
                return "Daily Statistics";

            default:
                return "Dashboard";
        }
    };

    return (
        <div className={cx(classes.root, classes.metalBackground)}>
            <TopBar onMobileNavOpen={() => setMobileNavOpen(true)} />
            <NavBar
                onMobileClose={() => setMobileNavOpen(false)}
                openMobile={isMobileNavOpen}
                setCoin={setCoin}
            />
            <div className={classes.metalWrapper}>
                <div className={classes.contentContainer}>
                    <div className={classes.content}>
                        <section
                            className="m-section"
                            style={{ paddingBlock: "unset", height: "unset" }}
                        >
                            <div
                                className="m-container"
                                style={{
                                    paddingLeft: 0,
                                    maxInlineSize: "1200px",
                                }}
                            >
                                <header
                                    className="m-heading has-space-m"
                                    style={{
                                        marginTop: mobileDevice ? 60 : 40,
                                        paddingBottom: 12,
                                    }}
                                >
                                    <div
                                        className="m-stack is-horizontal"
                                        style={{
                                            justifyContent: "flex-start",
                                            alignItems: "center",
                                        }}
                                    >
                                        <p
                                            className="m-heading is-size-xxl"
                                            style={{
                                                textTransform: "capitalize",
                                                paddingLeft: 12,
                                            }}
                                        >
                                            {handleLocationName()}
                                        </p>
                                        <p
                                            className={`m-text m-chip m-info`}
                                            style={{
                                                borderRadius: "12px",
                                                marginLeft: 8,
                                            }}
                                            data-color-status="info"
                                        >
                                            {subAccount}
                                        </p>
                                    </div>
                                </header>
                            </div>
                        </section>
                        <Container maxWidth="lg" style={{ paddingLeft: 0, marginTop: 0 }}>
                            <Suspense fallback={<LoadingScreen />}>
                                <Routes>
                                    <Route
                                        path="dashboard"
                                        element={
                                            <DashboardViewerComp
                                                {...children}
                                                apiKey={apiKey}
                                                authenticated={authenticated}
                                                subAccountSelection={subAccount}
                                                isViewerComponent={true}
                                            />
                                        }
                                    />
                                    <Route
                                        path="workers"
                                        element={
                                            <WorkersViewerComp
                                                apiKey={apiKey}
                                                authenticated={authenticated}
                                                subAccount={subAccount}
                                                workerStatus={workerStatus}
                                            />
                                        }
                                    />
                                    <Route
                                        path="daily-hashrate"
                                        element={
                                            <DailyHashrateViewerComp
                                                apiKey={apiKey}
                                                authenticated={authenticated}
                                                subAccount={subAccount}
                                            />
                                        }
                                    />
                                    <Route
                                        index
                                        element={
                                            <AppRedirectionComp
                                                apiKey={apiKey}
                                                authenticated={authenticated}
                                                subAccount={subAccount}
                                            />
                                        }
                                    />
                                </Routes>
                            </Suspense>
                        </Container>
                    </div>
                </div>
            </div>
            <Helmet>
                <script src="https://cy3d3zcbp37t.statuspage.io/embed/script.js"></script>
            </Helmet>
        </div>
    );
};

ViewerDashboardLayout.propTypes = {
    children: PropTypes.node,
};

export default ViewerDashboardLayout;
